import { useContext, useEffect, useMemo, useState } from "react";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import LeftMenu from "./menus/LeftMenu";
import HeaderBar from "./headers/HeaderBar";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  LinearProgress,
  Alert,
  MenuItem,
} from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";

export default function HelpfaqDesktop() {
  const { translate } = useContext(LanguageContext);
  const { currentUser } = useAuth();
  const [role, setRole] = useState("Normal User");
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [ttsInput, setTtsInput] = useState("");
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsError, setTtsError] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const [ttsVoice, setTtsVoice] = useState("alloy");
  const [ttsLang, setTtsLang] = useState("fr");
  const functions = getFunctions();
  const enableTts = false; // feature temporairement désactivée

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  useEffect(() => {
    if (!currentUser) return;

    const profileRef = doc(db, "profilsUsers", currentUser.uid);
    const unsub = onSnapshot(profileRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setRole(data.role || "Normal User");
      setCurrentUserProfile(data);

      if (typeof data.darkMode === "boolean") {
        try {
          const current = JSON.parse(
            localStorage.getItem("darkMode") ?? "null"
          );
          if (current !== data.darkMode) {
            localStorage.setItem("darkMode", JSON.stringify(data.darkMode));
            window.dispatchEvent(
              new CustomEvent("darkmode:changed", { detail: data.darkMode })
            );
          }
        } catch {}
      }
    });
    return () => unsub();
  }, [currentUser]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
      }}
    >
      <LeftMenu />

      <Box sx={{ width: "90%", boxSizing: "border-box", ml: "5px" }}>
        <HeaderBar
          title={translate("SupportTickets")}
          subtitle={translate("PleaseSendTicket")}
          showProjectSelect={false}
          userName={currentUser?.displayName}
          userRole={role}
        />

        <Box
          sx={{
            pl: "20px",
            display: "flex",
            flexDirection: "column",
            height: "90vh",
            overflowY: "auto",
            bgcolor: "background.default",
          }}
        >
          {/* Hero */}
          <Box sx={{ py: 8 }}>
            <Container maxWidth="md">
              <Typography
                variant="h3"
                fontWeight={700}
                align="center"
                gutterBottom
              >
                {translate("AboutHeroTitle")}
              </Typography>
              <Typography variant="h6" color="text.secondary" align="center">
                {translate("AboutHeroSubtitle")}
              </Typography>
            </Container>
          </Box>

          {/* Modèle économique */}
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <Container maxWidth="lg">
              <Typography
                variant="h4"
                fontWeight={700}
                align="center"
                gutterBottom
              >
                {translate("AboutModelTitle")}
              </Typography>
              <Grid
                container
                spacing={4}
                sx={{ mt: 2, justifyContent: "center" }}
              >
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 4,
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      {translate("FreePlanTitle")}
                    </Typography>
                    <Typography gutterBottom>
                      • {translate("FreePlanFeature1")}
                    </Typography>
                    <Typography gutterBottom>
                      • {translate("FreePlanFeature2")}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 4,
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      {translate("PremiumPlanTitle")}
                    </Typography>
                    <Typography gutterBottom>
                      • {translate("PremiumPlanFeature1")}
                    </Typography>
                    <Typography gutterBottom>
                      • {translate("PremiumPlanFeature2")}
                    </Typography>
                    <Typography gutterBottom>
                      • {translate("PremiumPlanFeature3")}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Pourquoi nous choisir */}
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <Container maxWidth="md">
              <Typography
                variant="h4"
                fontWeight={700}
                align="center"
                gutterBottom
              >
                {translate("WhyChooseTitle")}
              </Typography>
              <Grid
                container
                spacing={4}
                sx={{ mt: 2, justifyContent: "center" }}
              >
                {[1, 2, 3].map((i) => (
                  <Grid key={i} item xs={12} md={4}>
                    <Paper
                      sx={{
                        p: 3,
                        textAlign: "center",
                        borderRadius: 2,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {translate(`WhyChooseFeature${i}Title`)}
                      </Typography>
                      <Typography color="text.secondary">
                        {translate(`WhyChooseFeature${i}Desc`)}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>

          {/* Génération audio OpenAI temporairement désactivée */}
          {enableTts && (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <Container maxWidth="md">
                <Typography
                  variant="h4"
                  fontWeight={700}
                  align="center"
                  gutterBottom
                >
                  Générer un audio (FR)
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                  sx={{ mb: 3 }}
                >
                  Entrez une phrase en français, nous la convertissons en voix
                  féminine via OpenAI (modèle TTS récent).
                </Typography>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: "background.paper",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Texte à convertir (FR)"
                    multiline
                    minRows={3}
                    value={ttsInput}
                    onChange={(e) => setTtsInput(e.target.value)}
                    placeholder="Exemple : Bonjour, voici votre réponse en audio."
                  />
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        const text = ttsInput.trim();
                        if (text.length < 3) {
                          setTtsError("Le texte est trop court.");
                          return;
                        }
                        setTtsError("");
                        setTtsLoading(true);
                        try {
                          const generate = httpsCallable(
                            functions,
                            "generateFaqAudio"
                          );
                          const res = await generate({
                            text,
                            voice: ttsVoice,
                            lang: ttsLang,
                          });
                          const { audio, contentType } = res.data || {};
                          if (!audio) throw new Error("Audio manquant");
                          const byteCharacters = atob(audio);
                          const byteNumbers = new Array(byteCharacters.length);
                          for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                          }
                          const blob = new Blob(
                            [new Uint8Array(byteNumbers)],
                            {
                              type: contentType || "audio/mpeg",
                            }
                          );
                          if (audioUrl) URL.revokeObjectURL(audioUrl);
                          setAudioUrl(URL.createObjectURL(blob));
                        } catch (e) {
                          console.error(e);
                          setTtsError(
                            "Impossible de générer l'audio. Vérifiez la clé OpenAI côté fonctions."
                          );
                        } finally {
                          setTtsLoading(false);
                        }
                      }}
                      disabled={ttsLoading}
                    >
                      Générer l'audio
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <TextField
                      select
                      label="Voix"
                      size="small"
                      value={ttsVoice}
                      onChange={(e) => setTtsVoice(e.target.value)}
                      sx={{ minWidth: 140 }}
                    >
                      {["alloy", "verse", "nova", "shimmer"].map((v) => (
                        <MenuItem key={v} value={v}>
                          {v}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Langue"
                      size="small"
                      value={ttsLang}
                      onChange={(e) => setTtsLang(e.target.value)}
                      sx={{ minWidth: 140 }}
                    >
                      {[
                        ["fr", "Français"],
                        ["en", "English"],
                        ["es", "Español"],
                        ["de", "Deutsch"],
                        ["it", "Italiano"],
                        ["pt", "Português"],
                        ["nl", "Nederlands"],
                      ].map(([code, label]) => (
                        <MenuItem key={code} value={code}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Button
                    variant="outlined"
                    disabled={!audioUrl}
                    onClick={() => {
                      if (!audioUrl) return;
                      const a = document.createElement("a");
                      a.href = audioUrl;
                      a.download = "faq-audio.mp3";
                      a.click();
                    }}
                  >
                    Télécharger
                  </Button>
                  {ttsLoading && <LinearProgress />}
                  {ttsError && <Alert severity="error">{ttsError}</Alert>}
                  {audioUrl && !ttsError && (
                    <audio controls src={audioUrl} style={{ width: "100%" }} />
                  )}
                </Paper>
              </Container>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
